import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiInbox, FiTrendingUp, FiLock } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getReceipts, cancelReceipt,
  getDerasarPersonsAdmin, getDerasarPersons,
  sendReceiptEmail,
} from '../../services/api';
import { PageHeader, CancelConfirmModal } from '../../components/common';
import OnlinePaymentWarningModal from './OnlinePaymentWarningModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { usePdfDownload } from '../../pdf/usePdfDownload';
import { TOAST, ACTIONS, MODAL, NAV } from '../../constants/labels';
import { PAYMENT_LABEL } from './receiptsConfig';
import ReceiptsSummaryCards from './ReceiptsSummaryCards';
import ReceiptsFilters from './ReceiptsFilters';
import ReceiptsTable from './ReceiptsTable';

const ReceiptsPage = () => {
  const {
    activeDerasarId,
    user,
    isDerasarAdmin,
    activeDerasar,
    visibleFinancialYears,
    activeFinancialYearId,
    can,
  } = useAuth();
  const navigate = useNavigate();

  const [receipts, setReceipts] = useState([]);
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [onlineWarningItem, setOnlineWarningItem] = useState(null);

  /* filters */
  const [filterYear, setFilterYear]   = useState('');
  const [filterType, setFilterType]   = useState('');
  const [filterDonor, setFilterDonor] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  /* -- load ------------------------------------------------------------ */
  const loadMeta = useCallback(async () => {
    if (!activeDerasarId) return;
    try {
      const membersData = isDerasarAdmin
        ? await getDerasarPersonsAdmin(activeDerasarId)
        : await getDerasarPersons(activeDerasarId);
      const persons = Array.isArray(membersData.data) ? membersData.data : [];
      setMembers(persons.map((p) => ({ id: p.id, name: p.name })));
    } catch {
      // members are display-only — silent failure is acceptable
    }
  }, [activeDerasarId, isDerasarAdmin]);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const params = { derasarId: activeDerasarId };
      if (filterYear) params.financialYearId = filterYear;
      if (filterType) params.receiptType = filterType;
      if (filterFromDate) params.fromDate = filterFromDate;
      if (filterToDate) params.toDate = filterToDate;
      const rRes = await getReceipts(params);
      setReceipts(Array.isArray(rRes.data) ? rRes.data : []);
    } catch {
      toast.error(TOAST.loadFailed(PAYMENT_LABEL));
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId, filterYear, filterType, filterFromDate, filterToDate]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setFilterYear(activeFinancialYearId || '');
  }, [activeDerasarId, activeFinancialYearId]);

  const filterYearOptions = useMemo(() => visibleFinancialYears, [visibleFinancialYears]);

  const selectedFilterYear = useMemo(
    () => filterYearOptions.find((year) => String(year.id) === String(filterYear)) || null,
    [filterYearOptions, filterYear],
  );

  const filterMinDate = selectedFilterYear?.startDate?.slice(0, 10) || '';
  const filterMaxDate = selectedFilterYear?.endDate?.slice(0, 10) || '';

  useEffect(() => {
    if (!filterYear) {
      setFilterFromDate('');
      setFilterToDate('');
      return;
    }

    setFilterFromDate(filterMinDate || '');
    setFilterToDate(filterMaxDate || '');
  }, [filterYear, filterMinDate, filterMaxDate]);

  /* -- hooks ----------------------------------------------------------- */
  const { cancelModal, cancelReason, cancelling, openCancel, closeCancel, setReason, handleCancel } =
    useCancelConfirm(cancelReceipt, load, TOAST.cancelSuccess(PAYMENT_LABEL), { requireReason: false });

  const { download: downloadPdf } = usePdfDownload('receipt');

  const handleDownload = useCallback((r) => {
    downloadPdf({ receipt: r, derasar: activeDerasar?.derasarId, member: r.member });
  }, [downloadPdf, activeDerasar]);

  const handleSendEmail = useCallback(async (r) => {
    try {
      const res = await sendReceiptEmail(r.id);
      toast.success(res.data?.message || TOAST.emailSent);
    } catch (err) {
      toast.error(err.response?.data?.message || TOAST.emailFailed);
    }
  }, []);

  const clearFilters = () => {
    setFilterYear('');
    setFilterType('');
    setFilterDonor('');
    setFilterFromDate('');
    setFilterToDate('');
  };

  /* -- filtering ------------------------------------------------------- */
  const donorName = (r) => r.member?.name || (r.userId ? (members.find((m) => m.id === r.userId)?.name || null) : null);

  const visibleReceipts = useMemo(() => {
    if (!filterDonor.trim()) return receipts;
    const q = filterDonor.trim().toLowerCase();
    return receipts.filter((r) => {
      const name = r.receiptType === 'anonymous' ? 'anonymous' : (donorName(r) || '');
      return name.toLowerCase().includes(q);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts, filterDonor, members]);

  const totalAmount = visibleReceipts.reduce((s, r) => s + parseFloat(r.amountInr || 0), 0);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        icon={<FiInbox />}
        title={NAV.receipts}
        actions={
          <div style={{ display: 'flex', gap: 10 }}>
            {(!isDerasarAdmin && user?.role === 'user' && activeDerasar?.derasarId?.onlinePaymentEnabled) && (
              <button
                className="btn btn-primary btn-sm"
                style={{ background: 'var(--color-success)', borderColor: 'var(--color-success)' }}
                onClick={() => navigate('/pay-offers')}
              >
                <FiTrendingUp /> Pay Offers
              </button>
            )}
            {can('receipts', 'manage') && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/accounting/receipts/new')}
                disabled={!!selectedFilterYear?.isClosed}
                title={selectedFilterYear?.isClosed ? 'This financial year is locked — unlock it before adding new receipts.' : undefined}
              >
                {selectedFilterYear?.isClosed ? <FiLock /> : <FiPlus />} {ACTIONS.new(PAYMENT_LABEL)}
              </button>
            )}
          </div>
        }
      />

      <ReceiptsSummaryCards receipts={visibleReceipts} />

      <ReceiptsFilters
        years={filterYearOptions}
        filterYear={filterYear}
        filterType={filterType}
        filterDonor={filterDonor}
        filterFromDate={filterFromDate}
        filterToDate={filterToDate}
        minDate={filterMinDate}
        maxDate={filterMaxDate}
        onYearChange={(nextValue) => {
          setFilterYear(nextValue);
        }}
        onTypeChange={setFilterType}
        onDonorChange={setFilterDonor}
        onFromDateChange={setFilterFromDate}
        onToDateChange={setFilterToDate}
        onClearFilters={clearFilters}
      />

      <ReceiptsTable
        receipts={visibleReceipts}
        members={members}
        totalAmount={totalAmount}
        onDownload={handleDownload}
        onEmail={handleSendEmail}
        onEdit={(r) => navigate(`/accounting/receipts/${r.id}/edit`)}
        onCancelClick={(r) => {
          if (r.receiptType === 'online') {
            setOnlineWarningItem(r);
          } else {
            openCancel(r);
          }
        }}
        isDerasarAdmin={can('receipts', 'manage')}
      />

      <OnlinePaymentWarningModal
        item={onlineWarningItem}
        onClose={() => setOnlineWarningItem(null)}
        onConfirm={(item) => openCancel(item)}
      />

      <CancelConfirmModal
        entity={PAYMENT_LABEL}
        item={cancelModal}
        itemDisplay={cancelModal?.receiptNo}
        amount={cancelModal?.amountInr}
        extraNote={MODAL.cancel.journalEntryNote}
        reason={cancelReason}
        onReasonChange={setReason}
        onConfirm={handleCancel}
        onClose={closeCancel}
        cancelling={cancelling}
        showReason={false}
      />
    </div>
  );
};

export default ReceiptsPage;
